/**
 * 配達希望日が当日かを判定するための "YYYY-MM-DD" 文字列を返す。
 */
export const formatDateYmd = (date: Date): string => {
  // Date -> YYYY-MM-DD へ正規化
  return (
    date.getFullYear() +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getDate()).padStart(2, "0")
  );
};

/**
 * 当日配送時に、選択時刻が現在時刻より後かを判定する。
 * 分未選択時は「30分が未来なら有効」として扱う。
 */
export const validateStartTimeAfterNow = (
  requiredDeliveryDate: string,
  selectedHour: number | null | undefined,
  selectedMinuteLabel: string | null | undefined,
  now: Date = new Date()
): boolean => {
  // 判定対象日が当日でなければ現在時刻との比較は不要
  const nowDate = formatDateYmd(now);
  if (requiredDeliveryDate !== nowDate) {
    return true;
  }

  // 時が未選択なら判定不能のため無効
  if (selectedHour === null || selectedHour === undefined) {
    return false;
  }
  // 選択時が現在時より後なら有効
  if (selectedHour > now.getHours()) {
    return true;
  }
  // 選択時が現在時より前なら無効
  if (selectedHour < now.getHours()) {
    return false;
  }

  // 同時は分で比較。分がある場合は厳密比較
  if (selectedMinuteLabel !== null && selectedMinuteLabel !== undefined) {
    return Number(selectedMinuteLabel) > now.getMinutes();
  }
  // 分未選択時は「30分」が未来かどうかで暫定判定
  return now.getMinutes() < 30;
};

/**
 * 選択時刻が配達開始時刻以上かを判定する。
 * 分未選択時は、同一時であれば有効とする。
 */
export const validateStartTimeAfterDeliveryStart = (
  deliveryStartTime: string,
  selectedHour: number | null | undefined,
  selectedMinuteLabel: string | null | undefined
): boolean => {
  // 基準となる配達開始時刻を時・分へ分解
  const [startHourRaw, startMinuteRaw] = deliveryStartTime.split(":");
  const startHour = Number(startHourRaw);
  const startMinute = Number(startMinuteRaw);

  // 時未選択は無効
  if (selectedHour === null || selectedHour === undefined) {
    return false;
  }
  // 開始時より後の時は有効
  if (selectedHour > startHour) {
    return true;
  }
  // 開始時より前の時は無効
  if (selectedHour < startHour) {
    return false;
  }
  // 同時で分未選択の場合は、後続入力を許容して有効
  if (selectedMinuteLabel === null || selectedMinuteLabel === undefined) {
    return true;
  }
  // 同時は分を比較して開始分以上なら有効
  return Number(selectedMinuteLabel) >= startMinute;
};

/**
 * 選択時刻が受付終了時刻以内かを判定する。
 * 分未選択時は、同一時であれば有効とする。
 */
export const validateStartTimeBeforeDeliveryEnd = (
  deliveryEndTime: string,
  selectedHour: number | null | undefined,
  selectedMinuteLabel: string | null | undefined
): boolean => {
  // 基準となる受付終了時刻を時・分へ分解
  const [endHourRaw, endMinuteRaw] = deliveryEndTime.split(":");
  const endHour = Number(endHourRaw);
  const endMinute = Number(endMinuteRaw);

  // 時未選択は無効
  if (selectedHour === null || selectedHour === undefined) {
    return false;
  }
  // 終了時より前の時は有効
  if (selectedHour < endHour) {
    return true;
  }
  // 終了時より後の時は無効
  if (selectedHour > endHour) {
    return false;
  }
  // 同時で分未選択の場合は、後続入力を許容して有効
  if (selectedMinuteLabel === null || selectedMinuteLabel === undefined) {
    return true;
  }
  // 同時は分を比較して終了分以下なら有効
  return Number(selectedMinuteLabel) <= endMinute;
};
